import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, MapPin, Clock, Package, RefreshCw } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TZ = 'America/Costa_Rica';
const fecha = (iso) => new Date(iso).toLocaleDateString('es-CR', { timeZone: TZ, day: '2-digit', month: 'short', year: 'numeric' });
const hora = (iso) => new Date(iso).toLocaleTimeString('es-CR', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });

const Dato = ({ label, children, testId }) => (
  <div className="bg-gray-50 rounded-xl p-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-semibold text-gray-900 mt-0.5" data-testid={testId}>{children}</p>
  </div>
);

const Movimiento = ({ m, ultimo }) => (
  <li className="relative pl-6 pb-6 last:pb-0" data-testid="pkg-movimiento">
    <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 ${ultimo ? 'bg-[#FFE20A] border-[#194D3D]' : 'bg-white border-[#194D3D]/40'}`} />
    <p className={`font-medium ${ultimo ? 'text-[#194D3D]' : 'text-gray-800'}`}>{m.descripcion}</p>
    <p className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
      <span className="inline-flex items-center gap-1"><Clock size={12} />{fecha(m.fecha)} · {hora(m.fecha)}</span>
      {m.ubicacion && <span className="inline-flex items-center gap-1"><MapPin size={12} />{m.ubicacion}</span>}
    </p>
  </li>
);

const Historial = ({ estado, data, onRetry }) => {
  if (estado === 'cargando') return <p className="text-sm text-gray-500 py-6 text-center" data-testid="pkg-historial-loading">Cargando seguimiento...</p>;
  if (estado === 'error' || data?.disponible === false) {
    return (
      <div className="text-center py-6" data-testid="pkg-historial-error">
        <p className="text-sm text-gray-600 mb-3">No pudimos consultar el seguimiento en este momento.</p>
        <button onClick={onRetry} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#194D3D] text-white text-sm" data-testid="pkg-historial-retry"><RefreshCw size={14} />Reintentar</button>
      </div>
    );
  }
  if (!data.movimientos.length) {
    return <p className="text-sm text-gray-500 py-6 text-center" data-testid="pkg-historial-vacio">Estamos preparando la información de seguimiento de tu paquete.</p>;
  }
  return (
    <ol className="relative border-l-2 border-[#194D3D]/15 ml-2 pl-0" data-testid="pkg-historial">
      {data.movimientos.map((m, i) => <Movimiento key={`${m.fecha}-${i}`} m={m} ultimo={i === data.movimientos.length - 1} />)}
    </ol>
  );
};

export const PaqueteDetalle = ({ pkg, onClose, estadoLabel, estadoClase }) => {
  const [data, setData] = useState(null);
  const [estado, setEstado] = useState('cargando');
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    if (!pkg) return;
    let vigente = true;
    setEstado('cargando');
    const headers = { Authorization: `Bearer ${localStorage.getItem('sendhapy_token')}` };
    axios.get(`${BACKEND_URL}/api/cliente/paquetes/${encodeURIComponent(pkg.tracking)}/movimientos`, { headers })
      .then((r) => { if (vigente) { setData(r.data); setEstado('ok'); } })
      .catch(() => { if (vigente) setEstado('error'); });
    return () => { vigente = false; };
  }, [pkg, intento]);

  return (
    <Sheet open={!!pkg} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="pkg-detalle-sheet">
        {pkg && (
          <div className="p-5 sm:p-6">
            <button onClick={onClose} className="inline-flex items-center gap-2 text-sm font-medium text-[#194D3D] mb-5" data-testid="pkg-detalle-volver"><ArrowLeft size={16} />Volver a Mis Paquetes</button>
            <SheetHeader className="text-left space-y-2">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-xl bg-[#194D3D] text-[#FFE20A] flex items-center justify-center shrink-0"><Package size={22} /></span>
                <div className="min-w-0">
                  <SheetTitle className="text-lg leading-snug" data-testid="pkg-detalle-descripcion">{pkg.descripcion}</SheetTitle>
                  <SheetDescription className="font-mono text-xs break-all" data-testid="pkg-detalle-tracking">{pkg.tracking}</SheetDescription>
                </div>
              </div>
              <span className={`self-start px-3 py-1 rounded-full text-sm font-medium ${estadoClase(pkg.estado)}`} data-testid="pkg-detalle-estado">{estadoLabel(pkg.estado)}</span>
            </SheetHeader>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <Dato label="Peso facturable" testId="pkg-detalle-peso">{pkg.peso_kg ? `${pkg.peso_kg} kg` : 'Pendiente'}</Dato>
              <Dato label="Monto" testId="pkg-detalle-monto">{pkg.monto_usd ? `$${Number(pkg.monto_usd).toFixed(2)}` : 'Por calcular'}</Dato>
              <Dato label="Ubicación actual" testId="pkg-detalle-ubicacion">{estado === 'ok' && data?.ubicacion_actual ? data.ubicacion_actual : '—'}</Dato>
              <Dato label="Último movimiento" testId="pkg-detalle-ultimo">{estado === 'ok' && data?.ultimo_movimiento ? `${fecha(data.ultimo_movimiento)} · ${hora(data.ultimo_movimiento)}` : '—'}</Dato>
            </div>
            {pkg.requiere_permiso && <p className="text-xs text-amber-700 mt-3">Incluye permiso especial +${Number(pkg.recargo_permiso_usd).toFixed(2)}</p>}

            <h4 className="font-semibold text-[#194D3D] mt-8 mb-4">Historial de movimientos</h4>
            <Historial estado={estado} data={data} onRetry={() => setIntento((i) => i + 1)} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
